import { Button, Spin, Radio } from 'antd';
import { ArrowLeft, Close, Download } from 'assets/images';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import { exportFile } from 'util/export';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { PlusZoom, DefaultZoom, MinusZoom } from 'assets/icons';
import { CLOSE_CLEANING_REPORT_ROUTE_IMAGE_MODAL } from '../CleaningReportList/state/action';
import { selectCleaningReportsRouteImage } from '../CleaningReportList/state/selector';

export const RouteImageModal = () => {
  const {
    modal: { isOpen, selectedId, title },
    data
  } = useSelector((state) => selectCleaningReportsRouteImage(state));
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_NAMESPACES.CLEANING_REPORT);
  const [svgData, setSvgData] = useState(null);
  const minScale = 1;
  const maxScale = 8;
  const [currentScale, setCurrentScale] = useState(minScale);
  const [isS3Loading, setIsS3Loading] = useState(false);

  const isLoading = data?.[selectedId]?.loading;
  const imageUrl = data?.[selectedId]?.imageUrl;

  useEffect(() => {
    const fetchImgData = async () => {
      try {
        setIsS3Loading(true);
        const response = await fetch(imageUrl);
        const jsonData = await response.text();
        setSvgData(jsonData);
        setIsS3Loading(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching img data:', error);
      }
    };

    if (imageUrl) {
      fetchImgData();
    }
  }, [imageUrl]);

  const machineRouteSvgMapRef = (node) => {
    const svgNode = node?.querySelector('svg');
    if (!svgNode) return;
    const { x, y, width, height } = svgNode.getBBox();
    svgNode.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
  };

  const handleClose = () => {
    dispatch({
      type: CLOSE_CLEANING_REPORT_ROUTE_IMAGE_MODAL
    });
  };

  const renderBodyContent = () => {
    if (isLoading || isS3Loading) {
      return (
        <Spin data-testid="loading-spin" className="route-image-loading" />
      );
    }

    if (imageUrl && svgData) {
      return (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: svgData }}
          ref={machineRouteSvgMapRef}
          id="machine-route"
          className="route-svg-file"
        />
      );
    }

    return (
      <span className="no-route-image">{t('routeImageModal.noImage')}</span>
    );
  };

  if (!isOpen) {
    return null;
  }

  const handleDownload = async () => {
    const imageData = await convertSvgToPng();
    exportFile(imageData, `${title}.png`);
  };

  const convertSvgToPng = async () => {
    const svgNode = document
      .getElementById('machine-route')
      .querySelector('svg');
    const xml = new XMLSerializer().serializeToString(svgNode);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const base64Header = 'data:image/svg+xml;base64,';
    const image64 = base64Header + svg64;
    const { width, height } = svgNode.getBBox();
    return convertBase64SvgToBase64Png(image64, width, height);
  };

  const convertBase64SvgToBase64Png = (originalBase64, width, height) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        try {
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl);
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = originalBase64;
    });
  };

  return (
    <div className="route-image-modal">
      <div className="route-image-modal-mask" />
      <div className="route-image-modal-wrap">
        <TransformWrapper
          onTransformed={(ref) => setCurrentScale(ref.state.scale)}
          minScale={minScale}
          maxScale={maxScale}>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div className="route-image-header">
                <Button
                  className="route-image-go-back"
                  onClick={handleClose}
                  type="link"
                  icon={<ArrowLeft className="route-image-icon" />}
                />
                <span className="route-image-title">{title}</span>
                <div className="divider" />
                {!(isLoading || isS3Loading) && (
                  <Button
                    className="route-image-download-button"
                    type="link"
                    onClick={handleDownload}
                    icon={<Download className="route-image-icon" />}
                  />
                )}
                <Button
                  className="route-image-close-button"
                  onClick={handleClose}
                  type="link"
                  icon={<Close className="route-image-icon" />}
                />
              </div>

              <div className="route-image-body">
                <TransformComponent>{renderBodyContent()}</TransformComponent>
              </div>
              <Radio.Group className="route-image-footer">
                <Radio.Button onClick={() => zoomOut()}>
                  <MinusZoom
                    className={
                      currentScale !== minScale ? 'minus-zoom-not-min' : ''
                    }
                  />
                </Radio.Button>
                <Radio.Button onClick={() => resetTransform()}>
                  <DefaultZoom
                    className={
                      currentScale !== minScale ? 'default-zoom-not-min' : ''
                    }
                  />
                </Radio.Button>
                <Radio.Button onClick={() => zoomIn()}>
                  <PlusZoom
                    className={currentScale === maxScale ? 'plus-zoom-max' : ''}
                  />
                </Radio.Button>
              </Radio.Group>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
};
